import React, { useState, useRef, useLayoutEffect, useEffect } from "react";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { createPortal } from "react-dom";

interface TagsWithPopoverProps {
  tags: string | string[] | null | undefined;
  maxVisible?: number; // Maximum number of tags to show before truncating
  copyOnClick?: boolean; // If true, clicking a tag copies its content to clipboard
  /** When set, use this variant for all tags (single and multiple). Otherwise single tag uses "neutral", multiple use "default". */
  tagVariant?: "default" | "neutral";
  /**
   * Keep the inline trigger on one row (no wrap). Use in table cells so the
   * column does not grow in height.
   */
  triggerSingleRow?: boolean;
  /**
   * When true, inline chips share the trigger width and truncate long names
   * with ellipsis. Used by detail-card chip strips (`EditableTagList`).
   */
  constrainToContainer?: boolean;
}

export const TagsWithPopover: React.FC<TagsWithPopoverProps> = ({
  tags,
  maxVisible = 2,
  copyOnClick = false,
  tagVariant,
  triggerSingleRow = false,
  constrainToContainer = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [rect, setRect] = useState<DOMRect>();
  const [copiedTag, setCopiedTag] = useState<string | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Normalize tags first (before any early returns)
  const normalizedTags: string[] = React.useMemo(() => {
    if (tags == null) return [];
    if (typeof tags === "string") {
      const t = tags.trim();
      return t ? [t] : [];
    }
    if (Array.isArray(tags)) {
      return tags.filter((t) => typeof t === "string" && t.trim() !== "");
    }
    return [];
  }, [tags]);

  // Position popover on hover
  useLayoutEffect(() => {
    if (isHovered && triggerRef.current) {
      setRect(triggerRef.current.getBoundingClientRect());
    }
  }, [isHovered]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current);
      }
    };
  }, []);

  const handleTriggerEnter = () => {
    // Clear any pending hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setIsHovered(true);
  };

  const handleTriggerLeave = () => {
    // Use a small delay to allow mouse to move to popover
    // If mouse enters popover, the timeout will be cleared
    hideTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
      hideTimeoutRef.current = null;
    }, 150);
  };

  const handlePopoverEnter = () => {
    // Clear any pending hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setIsHovered(true);
  };

  const handlePopoverLeave = () => {
    setIsHovered(false);
  };

  const handleCopyTag = async (tagText: string) => {
    if (!copyOnClick) return;
    
    try {
      await navigator.clipboard.writeText(tagText);
      setCopiedTag(tagText);
      
      // Clear any existing timeout
      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current);
      }
      
      // Hide "Copied!" message after 2 seconds
      copiedTimeoutRef.current = setTimeout(() => {
        setCopiedTag(null);
        copiedTimeoutRef.current = null;
      }, 2000);
    } catch (err) {
      console.error("Failed to copy tag:", err);
    }
  };

  // Now we can do early returns after hooks are called
  if (
    normalizedTags.length === 0
  ) {
    return <span className="text-semantic-text-secondary">-</span>;
  }

  // If single tag (string), render it with the same styling as array tags
  if (normalizedTags.length === 1) {
    const handleCopySingleTag = async () => {
      if (!copyOnClick) return;
      try {
        await navigator.clipboard.writeText(normalizedTags[0]);
        setCopiedTag(normalizedTags[0]);
        if (copiedTimeoutRef.current) {
          clearTimeout(copiedTimeoutRef.current);
        }
        copiedTimeoutRef.current = setTimeout(() => {
          setCopiedTag(null);
          copiedTimeoutRef.current = null;
        }, 2000);
      } catch (err) {
        console.error("Failed to copy tag:", err);
      }
    };

    return (
      <div
        className={`flex items-center gap-1 relative min-w-0 ${triggerSingleRow ? "flex-nowrap" : "flex-wrap"}`}
      >
        <GenericTag
          variant={tagVariant ?? "neutral"}
          label={normalizedTags[0]}
          size="small"
          onClick={copyOnClick ? handleCopySingleTag : undefined}
          className={copyOnClick ? "cursor-pointer" : ""}
        />
        {copiedTag === normalizedTags[0] && (
          <span className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs text-semantic-text-success bg-semantic-bg-primary px-2 py-0.5 rounded border border-semantic-border-secondary whitespace-nowrap z-10">
            Copied!
          </span>
        )}
      </div>
    );
  }

  // Now we know it's an array
  const tagsArray = normalizedTags;

  // Calculate visible tags and remaining tags
  const visibleTags = tagsArray.slice(0, maxVisible);
  const remainingTags = tagsArray.slice(maxVisible); // Tags not visible
  const remainingCount = remainingTags.length;
  const showMoreIndicator = remainingCount > 0;

  const shouldShowPopover = isHovered && showMoreIndicator;

  const tagWrapperClass = constrainToContainer
    ? "min-w-0 max-w-[200px] flex-shrink-0"
    : "relative";

  const triggerOverflowClass = constrainToContainer
    ? "overflow-hidden"
    : "";

  // Calculate popover position (below the tags)
  const popoverStyle: React.CSSProperties = {
    position: "fixed",
    zIndex: 9999,
    maxWidth: "300px",
  };

  if (rect && shouldShowPopover) {
    const gap = 8;
    popoverStyle.top = rect.bottom + gap;
    popoverStyle.left = rect.left;
    popoverStyle.transform = "translateY(0)";
  }

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={handleTriggerEnter}
        onMouseLeave={handleTriggerLeave}
        className={`flex items-center gap-1 min-w-0 justify-end ${
          triggerSingleRow ? "flex-nowrap" : "flex-wrap"
        } ${triggerOverflowClass}`}
      >
        {/* Visible tags */}
        {visibleTags.map((tag, index) => (
          <div key={index} className={tagWrapperClass}>
            <GenericTag
              variant={tagVariant ?? "default"}
              label={tag}
              size="small"
              onClick={copyOnClick ? () => handleCopyTag(tag) : undefined}
              className={`w-fit max-w-full min-w-0 ${copyOnClick ? "cursor-pointer" : ""}`}
              truncateLabel={constrainToContainer}
              tooltip={constrainToContainer ? tag : undefined}
            />
            {copiedTag === tag && (
              <span className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs text-semantic-text-success bg-semantic-bg-primary px-2 py-0.5 rounded border border-semantic-border-secondary whitespace-nowrap z-10">
                Copied!
              </span>
            )}
          </div>
        ))}
        
        {/* "+X" indicator */}
        {showMoreIndicator && (
          <GenericTag
            variant={tagVariant ?? "default"}
            label={`+${remainingCount}`}
            size="small"
          />
        )}
      </div>

      {/* Popover with all tags */}
      {shouldShowPopover &&
        createPortal(
          <div
            ref={popoverRef}
            onMouseEnter={handlePopoverEnter}
            onMouseLeave={handlePopoverLeave}
            style={popoverStyle}
            className="
              bg-semantic-bg-primary
              rounded-md
              p-3
              shadow-lg
              border border-semantic-border-primary
              min-w-[200px]
            "
          >
            <div className="flex flex-wrap gap-1.5">
              {remainingTags.map((tag, index) => (
                <div key={index} className="relative">
                  <GenericTag
                    variant={tagVariant ?? "default"}
                    label={tag}
                    size="small"
                    onClick={copyOnClick ? () => handleCopyTag(tag) : undefined}
                    className={copyOnClick ? "cursor-pointer" : ""}
                  />
                  {copiedTag === tag && (
                    <span className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs text-semantic-text-success bg-semantic-bg-primary px-2 py-0.5 rounded border border-semantic-border-secondary whitespace-nowrap z-[10001]">
                      Copied!
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

